const OfferEngine = (() => {
  const SHAPE_CART = 'A';
  const SHAPE_LINE = 'B';
  const SHAPE_BUNDLE = 'C';
  const SHAPE_REWARD = 'D';
  const SUPPORTED_SHAPES = [SHAPE_CART, SHAPE_LINE, SHAPE_BUNDLE, SHAPE_REWARD];

  const POLICY_DEFAULTS = {
    stackingPolicy: 'greedy_stack',
    stackLimit: null,
    percentageBase: 'running',
    thresholdBase: 'original',
    bundleBlocksLineDiscount: true,
  };

  const REASON = {
    INACTIVE: 'inactive',
    REMOVED_BY_SHOPPER: 'removed_by_shopper',
    SHAPE_NOT_SUPPORTED: 'shape_not_supported',
    CODE_NOT_APPLIED: 'code_not_applied',
    NOT_FIRST_TIME: 'not_first_time_customer',
    BELOW_MIN_AMOUNT: 'below_min_cart_amount',
    ABOVE_MAX_AMOUNT: 'above_max_cart_amount',
    BELOW_MIN_QUANTITY: 'below_min_cart_quantity',
    CONDITION_PRODUCTS_MISSING: 'condition_products_missing',
    CONDITION_UNCONFIGURED: 'condition_unconfigured',
    NO_MATCHING_LINES: 'no_matching_lines',
    NOT_ENOUGH_ITEMS: 'not_enough_matching_items',
    WOULD_RAISE_PRICE: 'bundle_would_raise_price',
    UNITS_ALREADY_CLAIMED: 'units_already_claimed',
    ZERO_VALUE: 'zero_value',
    CANNOT_COMBINE: 'cannot_combine',
    STACK_LIMIT_REACHED: 'stack_limit_reached',
    NOT_BEST_OFFER: 'not_best_offer',
  };

  const CONFIDENCE = {
    CONFIRMED: 'confirmed',
    PENDING: 'pending_checkout_verification',
  };

  /* ------------------------------------------------------------------- math */

  const percentOf = (basePaise, percent) => Math.round((basePaise * percent) / 100);

  /**
   * Split `total` into integer parts proportional to `weights`, summing exactly
   * to `total`. Remainder goes to the earliest entries so the result is stable.
   */
  const distribute = (total, weights) => {
    const sum = weights.reduce((a, b) => a + b, 0);
    if (sum <= 0) return weights.map(() => 0);

    const parts = weights.map((weight) => Math.floor((total * weight) / sum));
    let remainder = total - parts.reduce((a, b) => a + b, 0);

    for (let i = 0; remainder > 0 && i < parts.length; i += 1) {
      parts[i] += 1;
      remainder -= 1;
    }
    return parts;
  };


  const matchesScope = (target, scopeType, variantIds, collectionHandles) => {
    if (scopeType === 'products') {
      return (variantIds ?? []).includes(target.variantId);
    }
    if (scopeType === 'collections') {
      const wanted = collectionHandles ?? [];
      return (target.collections ?? []).some((handle) => wanted.includes(handle));
    }
    return false;
  };

  const lineMatchesScope = (offer, line) => matchesScope(
    line, offer.scopeType, offer.scopeVariantIds, offer.scopeCollectionHandles,
  );

  const matchingLines = (offer, lines) => lines.filter((line) => lineMatchesScope(offer, line));

  const expandUnits = (lines) => lines.flatMap((line) => Array.from(
    { length: line.quantity },
    (_, index) => ({
      id: `${line.key}#${index}`,
      lineKey: line.key,
      price: line.unitPrice,
      variantId: line.variantId,
      collections: line.collections ?? [],
    }),
  ));

  const sortUnits = (units, rule) => [...units].sort((a, b) => (
    rule === 'most_expensive_first' ? b.price - a.price : a.price - b.price
  ));

  const cartContainsConditionProducts = (offer, lines) => {
    const variantIds = offer.conditionVariantIds ?? [];
    const collectionHandles = offer.conditionCollectionHandles ?? [];

    if (!variantIds.length && !collectionHandles.length) return null; // unconfigured

    return lines.some((line) => (
      variantIds.includes(line.variantId)
      || (line.collections ?? []).some((handle) => collectionHandles.includes(handle))
    ));
  };

  /* ----------------------------------------------------------- combinability */

  const allows = (offer, other) => {
    if (offer.combineMode === 'all') return true;
    if (offer.combineMode === 'none') return false;
    return (offer.combineWith ?? []).includes(other.handle);
  };

  const canStack = (a, b) => allows(a, b) && allows(b, a);

  const codeApplied = (offer, appliedCodes) => {
    if (offer.code && appliedCodes.includes(String(offer.code).toUpperCase())) return true;
    if (offer.codeHash && appliedCodes.includes(String(offer.codeHash).toLowerCase())) return true;
    return false;
  };

  /* -------------------------------------------------------------- evaluation */

  const evaluateCartOffer = (offer, basePaise) => {
    let amount = offer.discountUnit === 'percentage'
      ? percentOf(basePaise, offer.discountValue)
      : offer.discountValue;

    amount = Math.min(amount, basePaise);
    if (offer.maxDiscountAmount != null) amount = Math.min(amount, offer.maxDiscountAmount);
    return Math.max(amount, 0);
  };

  const evaluateLineOffer = (offer, lines, baseFor) => {
    const targets = matchingLines(offer, lines);
    if (!targets.length) return null;

    let perLine = targets.map((line) => {
      const base = baseFor(line);
      if (base <= 0) return 0;

      const amount = offer.discountUnit === 'percentage'
        ? percentOf(base, offer.discountValue)
        // Flat amounts are per item, per Shopflo's "Rs 75 off on each item" example.
        : offer.discountValue * line.quantity;

      return Math.min(amount, base);
    });

    let total = perLine.reduce((a, b) => a + b, 0);

    if (offer.maxDiscountAmount != null && total > offer.maxDiscountAmount) {
      perLine = distribute(offer.maxDiscountAmount, perLine);
      total = offer.maxDiscountAmount;
    }

    return {
      amount: total,
      perLine: targets.map((line, i) => ({ lineKey: line.key, discount: perLine[i] })),
      lineKeys: targets.map((line) => line.key),
    };
  };


  /**
   * Shape C — fixed-price bundle, "any N from this scope for one price".
   */
  const evaluateBundleOffer = (offer, units, claimedUnitIds) => {
    const size = offer.bundleSize;
    if (!size || offer.bundlePrice == null) return null;

    const pool = sortUnits(
      units.filter((unit) => (
        !claimedUnitIds.includes(unit.id)
        && matchesScope(unit, offer.scopeType, offer.scopeVariantIds, offer.scopeCollectionHandles)
      )),
      offer.selectionRule,
    );

    if (pool.length < size) return null;

    const maxUses = offer.usesPerOrder ?? Math.floor(pool.length / size);
    const perUnit = [];
    const claimed = [];
    let amount = 0;

    for (let use = 0; use < maxUses; use += 1) {
      const group = pool.slice(use * size, (use * size) + size);
      if (group.length < size) break;

      const groupTotal = group.reduce((sum, unit) => sum + unit.price, 0);
      const discount = groupTotal - offer.bundlePrice;
      if (discount <= 0) break;

      const shares = distribute(discount, group.map((unit) => unit.price));
      group.forEach((unit, index) => perUnit.push({ unit, discount: shares[index] }));
      claimed.push(...group.map((unit) => unit.id));
      amount += discount;
    }

    if (amount <= 0) return { amount: 0, blocked: REASON.WOULD_RAISE_PRICE };
    return { amount, perUnit, claimedUnitIds: claimed };
  };

  /**
   * Shape D — Buy X Get Y, "buy N from this scope, get M from that scope free or discounted".
   */
  const evaluateRewardOffer = (offer, units, claimedUnitIds) => {
    const rewardQuantity = offer.rewardQuantity ?? 1;
    const triggerMin = offer.triggerMinQuantity ?? 0;

    const free = units.filter((unit) => !claimedUnitIds.includes(unit.id));

    const triggerPool = free.filter((unit) => matchesScope(
      unit, offer.triggerScopeType, offer.triggerScopeVariantIds, offer.triggerScopeCollectionHandles,
    ));
    const rewardPool = sortUnits(
      free.filter((unit) => matchesScope(
        unit, offer.rewardScopeType, offer.rewardScopeVariantIds, offer.rewardScopeCollectionHandles,
      )),
      offer.selectionRule,
    );

    if (!rewardPool.length) return null;
    if (triggerPool.length < triggerMin + rewardQuantity) return null;

    /*
     * usesPerOrder is a ceiling, not an instruction to apply the offer that many times: on its
     * own, a cap of 5 on a three-item cart freed all three. The lower of the two wins.
     */
    const earnedUses = Math.floor(triggerPool.length / (triggerMin + rewardQuantity));
    const allowedUses = offer.usesPerOrder ?? earnedUses;
    const maxUses = Math.min(earnedUses, allowedUses);

    const perUnit = [];
    const claimed = [];
    let amount = 0;

    for (let use = 0; use < maxUses; use += 1) {
      const group = rewardPool.slice(use * rewardQuantity, (use * rewardQuantity) + rewardQuantity);
      if (group.length < rewardQuantity) break;

      for (const unit of group) {
        let discount;
        if (offer.rewardType === 'fixed_price') {
          discount = Math.max(unit.price - (offer.rewardValue ?? 0), 0);
        } else if (offer.rewardType === 'percentage') {
          discount = percentOf(unit.price, offer.rewardValue ?? 0);
        } else {
          discount = unit.price;
        }
        if (discount <= 0) continue;
        perUnit.push({ unit, discount });
        claimed.push(unit.id);
        amount += discount;
      }
    }

    if (amount <= 0) return null;
    return { amount, perUnit, claimedUnitIds: claimed };
  };

  /* ----------------------------------------------------------------- ranking */

  // Highest value first, then handle ascending so the result is deterministic.
  const byValueThenHandle = (a, b) => (
    b.value !== a.value ? b.value - a.value : (a.offer.handle < b.offer.handle ? -1 : 1)
  );

  /* -------------------------------------------------------------------- main */

  const evaluate = (cart, offers = [], context = {}) => {
    const policies = { ...POLICY_DEFAULTS, ...(context.policies ?? {}) };
    // Keep both cases: typed codes are compared upper-cased, hashes lower-cased.
    const appliedCodes = (context.appliedCodes ?? []).flatMap((code) => {
      const trimmed = String(code).trim();
      return [trimmed.toUpperCase(), trimmed.toLowerCase()];
    });
    const {
      customerIsFirstTime = null,
      suppressedHandles = [],
      forcedHandles = [],
    } = context;

    const lines = cart.lines ?? [];
    const baseSubtotal = cart.subtotal ?? lines.reduce((sum, line) => sum + line.linePrice, 0);
    const itemCount = cart.itemCount ?? lines.reduce((sum, line) => sum + line.quantity, 0);

    const units = expandUnits(lines);
    const trace = [];
    const rejected = [];
    const nudges = [];

    const reject = (offer, reason, detail = null) => {
      rejected.push({ handle: offer.handle, reason, detail });
      trace.push({ handle: offer.handle, stage: 'rejected', reason, detail });
    };

    /* -- eligibility ------------------------------------------------------- */

    const passesEligibility = (offer) => {
      if (offer.customerEligibility !== 'first_time') return true;
      // A typed code is accepted and verified by Shopflo at checkout. An automatic offer we cannot verify is skipped, because auto-applying it would over-promise.
      if (offer.mode === 'code') return true;
      return customerIsFirstTime === true;
    };

    const conditionReason = (offer, subtotalForConditions) => {
      switch (offer.purchaseConditionType) {
        case 'cart_amount':
          if (offer.minCartAmount != null && subtotalForConditions < offer.minCartAmount) {
            return REASON.BELOW_MIN_AMOUNT;
          }
          if (offer.maxCartAmount != null && subtotalForConditions > offer.maxCartAmount) {
            return REASON.ABOVE_MAX_AMOUNT;
          }
          return null;
        case 'cart_quantity':
          if (offer.minCartQuantity != null && itemCount < offer.minCartQuantity) {
            return REASON.BELOW_MIN_QUANTITY;
          }
          return null;
        case 'products_in_cart': {
          const present = cartContainsConditionProducts(offer, lines);
          if (present === null) return REASON.CONDITION_UNCONFIGURED;
          return present ? null : REASON.CONDITION_PRODUCTS_MISSING;
        }
        default:
          return null;
      }
    };

    /* -- stage 1 and 2: filter, then evaluate provisional value ------------- */

    const candidates = [];

    for (const offer of offers) {
      if (offer.isActive === false) { reject(offer, REASON.INACTIVE); continue; }

      // A shopper who removed an offer keeps it off until they apply it again.
      if (suppressedHandles.includes(offer.handle)) {
        reject(offer, REASON.REMOVED_BY_SHOPPER);
        continue;
      }

      if (!SUPPORTED_SHAPES.includes(offer.shape)) {
        reject(offer, REASON.SHAPE_NOT_SUPPORTED, `shape ${offer.shape}`);
        continue;
      }

      if (
        offer.mode === 'code'
        && !forcedHandles.includes(offer.handle)
        && !codeApplied(offer, appliedCodes)
      ) {
        reject(offer, REASON.CODE_NOT_APPLIED);
        continue;
      }

      if (!passesEligibility(offer)) { reject(offer, REASON.NOT_FIRST_TIME); continue; }

      // Line-level conditions always read the undiscounted subtotal. Cart-level conditions follow thresholdBase, and are re-checked after line discounts when that policy is 'running'.
      const conditionFail = conditionReason(offer, baseSubtotal);
      if (conditionFail) {
        reject(offer, conditionFail);
        if (conditionFail === REASON.BELOW_MIN_AMOUNT && isNudgeable(offer, lines)) {
          nudges.push({
            handle: offer.handle,
            label: offer.title,
            amountRemaining: offer.minCartAmount - baseSubtotal,
          });
        }
        continue;
      }

      let value = 0;
      let evaluation = null;

      if (offer.shape === SHAPE_LINE) {
        evaluation = evaluateLineOffer(offer, lines, (line) => line.linePrice);
        if (!evaluation) { reject(offer, REASON.NO_MATCHING_LINES); continue; }
        value = evaluation.amount;
      } else if (offer.shape === SHAPE_BUNDLE) {
        evaluation = evaluateBundleOffer(offer, units, []);
        if (!evaluation) { reject(offer, REASON.NOT_ENOUGH_ITEMS); continue; }
        if (evaluation.blocked) { reject(offer, evaluation.blocked); continue; }
        value = evaluation.amount;
      } else if (offer.shape === SHAPE_REWARD) {
        evaluation = evaluateRewardOffer(offer, units, []);
        if (!evaluation) { reject(offer, REASON.NOT_ENOUGH_ITEMS); continue; }
        value = evaluation.amount;
      } else {
        value = evaluateCartOffer(offer, baseSubtotal);
      }

      if (value <= 0) { reject(offer, REASON.ZERO_VALUE); continue; }

      candidates.push({ offer, value, evaluation });
      trace.push({ handle: offer.handle, stage: 'candidate', provisionalValue: value });
    }

    /* -- stage 3: rank ------------------------------------------------------ */

    candidates.sort(byValueThenHandle);

    /* -- stage 4: resolve --------------------------------------------------- */
    // No line claiming here. Shopflo states combinable discounts may apply to the same item, so two shape B offers hitting one line is legal. Claim tracking becomes necessary only when bundles arrive.

    const accepted = [];
    const claimedUnitIds = [];

    for (const candidate of candidates) {
      if (policies.stackingPolicy === 'best_single' && accepted.length >= 1) {
        reject(candidate.offer, REASON.NOT_BEST_OFFER, `beaten by ${accepted[0].offer.handle}`);
        continue;
      }

      if (policies.stackLimit != null && accepted.length >= policies.stackLimit) {
        reject(candidate.offer, REASON.STACK_LIMIT_REACHED, `limit ${policies.stackLimit}`);
        continue;
      }

      const blocker = accepted.find(({ offer }) => !canStack(offer, candidate.offer));
      if (blocker) {
        reject(candidate.offer, REASON.CANNOT_COMBINE, `blocked by ${blocker.offer.handle}`);
        continue;
      }

      if (candidate.offer.shape === SHAPE_BUNDLE || candidate.offer.shape === SHAPE_REWARD) {
        const evaluator = candidate.offer.shape === SHAPE_BUNDLE
          ? evaluateBundleOffer
          : evaluateRewardOffer;
        const reEvaluated = evaluator(candidate.offer, units, claimedUnitIds);

        if (!reEvaluated || reEvaluated.blocked || reEvaluated.amount <= 0) {
          reject(candidate.offer, REASON.UNITS_ALREADY_CLAIMED);
          continue;
        }
        candidate.evaluation = reEvaluated;
        candidate.value = reEvaluated.amount;
      }

      accepted.push(candidate);
      if (candidate.evaluation?.claimedUnitIds) {
        claimedUnitIds.push(...candidate.evaluation.claimedUnitIds);
      }
      trace.push({ handle: candidate.offer.handle, stage: 'accepted' });
    }

    /* -- stage 5: apply, line-level before cart-level ----------------------- */

    const lineDiscounts = new Map();   // lineKey -> paise
    const lineOffers = new Map();      // lineKey -> [{ handle, amount }]
    const appliedOffers = [];
    const cartAdjustments = [];

    const discountedSoFar = (lineKey) => lineDiscounts.get(lineKey) ?? 0;

    const lineBaseFor = (line) => (
      policies.percentageBase === 'original'
        ? line.linePrice
        : line.linePrice - discountedSoFar(line.key)
    );

    /* Bundles first: they set the price of the units they cover, and a following percentage should not also discount those units. */
    const bundledLineKeys = new Set();

    for (const candidate of accepted.filter(({ offer }) => (
      offer.shape === SHAPE_BUNDLE || offer.shape === SHAPE_REWARD
    ))) {
      const { offer, evaluation } = candidate;
      if (!evaluation?.perUnit?.length) continue;

      for (const { unit, discount } of evaluation.perUnit) {
        if (discount <= 0) continue;
        lineDiscounts.set(unit.lineKey, discountedSoFar(unit.lineKey) + discount);
        lineOffers.set(unit.lineKey, [
          ...(lineOffers.get(unit.lineKey) ?? []),
          { handle: offer.handle, amount: discount },
        ]);
        if (offer.shape === SHAPE_BUNDLE) bundledLineKeys.add(unit.lineKey);
      }

      appliedOffers.push(describe(offer, evaluation.amount));
    }

    /* A line whose units a bundle repriced is skipped by percentage offers when `bundleBlocksLineDiscount` is on, so the same unit is never discounted twice. */
    const lineDiscountable = policies.bundleBlocksLineDiscount
      ? lines.filter((line) => !bundledLineKeys.has(line.key))
      : lines;

    for (const { offer } of accepted.filter(({ offer }) => offer.shape === SHAPE_LINE)) {
      const evaluation = evaluateLineOffer(offer, lineDiscountable, lineBaseFor);
      if (!evaluation || evaluation.amount <= 0) {
        reject(offer, REASON.ZERO_VALUE, 'nothing left to discount on matching lines');
        continue;
      }

      for (const { lineKey, discount } of evaluation.perLine) {
        if (discount <= 0) continue;
        lineDiscounts.set(lineKey, discountedSoFar(lineKey) + discount);
        lineOffers.set(lineKey, [...(lineOffers.get(lineKey) ?? []), { handle: offer.handle, amount: discount }]);
      }

      appliedOffers.push(describe(offer, evaluation.amount));
    }

    const lineDiscountTotal = [...lineDiscounts.values()].reduce((a, b) => a + b, 0);
    const postLineSubtotal = baseSubtotal - lineDiscountTotal;

    let runningBase = policies.percentageBase === 'original' ? baseSubtotal : postLineSubtotal;
    let remaining = postLineSubtotal;

    for (const { offer } of accepted.filter(({ offer }) => offer.shape === SHAPE_CART)) {
      // Re-check cart-level conditions against the post-line subtotal when the threshold policy says to.
      if (policies.thresholdBase === 'running') {
        const conditionFail = conditionReason(offer, postLineSubtotal);
        if (conditionFail) { reject(offer, conditionFail, 'after line discounts'); continue; }
      }

      const amount = Math.min(evaluateCartOffer(offer, runningBase), remaining);
      if (amount <= 0) { reject(offer, REASON.ZERO_VALUE, 'nothing left to discount'); continue; }

      remaining -= amount;
      if (policies.percentageBase !== 'original') runningBase -= amount;

      cartAdjustments.push({ handle: offer.handle, label: offer.title, amount });
      appliedOffers.push(describe(offer, amount));
    }

    /* -- stage 6: assemble -------------------------------------------------- */

    const lineAdjustments = lines.map((line) => {
      const discount = discountedSoFar(line.key);
      return {
        lineKey: line.key,
        originalTotal: line.linePrice,
        discount,
        adjustedTotal: line.linePrice - discount,
        offers: lineOffers.get(line.key) ?? [],
      };
    });

    const cartDiscountTotal = cartAdjustments.reduce((sum, { amount }) => sum + amount, 0);
    const totalDiscount = lineDiscountTotal + cartDiscountTotal;

    nudges.sort((a, b) => a.amountRemaining - b.amountRemaining);

    return {
      currency: cart.currency ?? 'INR',
      policies,
      baseSubtotal,
      itemCount,
      lineAdjustments,
      cartAdjustments,
      appliedOffers,
      totalDiscount,
      estimatedSubtotal: baseSubtotal - totalDiscount,
      nudges,
      rejected,
      trace,
    };
  };

  const describe = (offer, amount) => ({
    handle: offer.handle,
    label: offer.title,
    shape: offer.shape,
    mode: offer.mode,
    amount,
    isPublic: offer.isPublic === true,
    // A first-time-customer code is accepted on trust; only Shopflo can verify it.
    confidence: offer.customerEligibility === 'first_time' && offer.mode === 'code'
      ? CONFIDENCE.PENDING
      : CONFIDENCE.CONFIRMED,
  });

  /* Only nudge toward an offer the shopper can actually reach with what is already in the cart. Nudging for a line-level offer whose products are absent would promise something that spending more cannot unlock */
  const isNudgeable = (offer, lines) => (
    offer.shape !== SHAPE_LINE || matchingLines(offer, lines).length > 0
  );

  return {
    evaluate,
    POLICY_DEFAULTS,
    REASON,
    CONFIDENCE,
    SHAPE_CART,
    SHAPE_LINE,
    internal: { distribute, percentOf, canStack, matchingLines, codeApplied },
  };
})();

if (typeof window !== 'undefined') window.OfferEngine = OfferEngine;
if (typeof module !== 'undefined' && module.exports) module.exports = OfferEngine;
